import { memo, RefObject } from "react";
import clsx from "clsx";
import { DraggableAttributes, DraggableSyntheticListeners, useDraggable, useDroppable } from "@dnd-kit/core";
import { motion, Transition } from "framer-motion";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DashboardCard } from "./DashboardCard";
import i18n from "../../i18n";
import { MovingOutline } from "./MovingOutline";
import { ResizeHandle } from "./ResizeHandle";
import { DashboardPlacement } from "./dashboardCards";
import {
  CARD_HEIGHTS,
  CardHeight,
  CardSize,
  GRID_COLUMNS,
  MIN_CARD_SPAN,
  nearestHeight,
  pxToSpan,
  stepHeight,
  stepSpan,
} from "./dashboardLayout";

const CARD_TRANSITION: Transition = {
  duration: 0.15,
  ease: "easeOut",
  layout: { type: "spring", stiffness: 500, damping: 40 },
};

interface GridCardProps {
  placement: DashboardPlacement;
  height: CardHeight;
  grid: RefObject<HTMLElement | null>;
  editing: boolean;
  onHide: () => void;
  onResize: (size: Partial<CardSize>) => void;
}

type Box = { box: RefObject<HTMLElement | null> };

const CardBody = memo(function CardBody({ placement, height, grid, box, editing, onHide, onResize, listeners, attributes, setActivatorNodeRef }: GridCardProps & Box & {
  listeners: DraggableSyntheticListeners;
  attributes: DraggableAttributes;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
}) {
  const { card } = placement;

  const resizeTo = (size: Partial<CardSize>) => {
    if ((size.w ?? placement.w) !== placement.w || (size.h ?? placement.h) !== placement.h) onResize(size);
  };

  const spanFor = (widthPx: number) => {
    const row = grid.current;
    if (!row) return placement.w;
    return pxToSpan(widthPx, row.getBoundingClientRect().width, parseFloat(getComputedStyle(row).columnGap) || 0);
  };

  return (
    <DashboardCard
      title={card.title}
      linkTo={card.linkTo}
      linkTooltip={card.linkTooltip}
      linkArea={card.linkArea}
      height={height}
      onHide={onHide}
      editing={editing}
      dragListeners={editing ? listeners : undefined}
      dragHandle={editing && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          aria-label={i18n.t("frame.move", { ns: "dashboard", title: card.title })}
          className="px-2 py-1 cursor-grab text-lg text-theme-text-muted hover:text-theme-text"
        >
          <FontAwesomeIcon icon={faGripVertical} />
        </button>
      )}
      editControls={editing && (
        <>
          <MovingOutline />
          <ResizeHandle
            axis="x"
            label={i18n.t("dashboard:frame.resizeWidth", { title: card.title })}
            value={placement.w}
            min={MIN_CARD_SPAN}
            max={GRID_COLUMNS}
            box={box}
            grid={grid}
            onDrag={widthPx => resizeTo({ w: spanFor(widthPx) })}
            onStep={delta => resizeTo({ w: stepSpan(placement.w, delta) })}
          />
          <ResizeHandle
            axis="y"
            label={i18n.t("dashboard:frame.resizeHeight", { title: card.title })}
            value={CARD_HEIGHTS.indexOf(placement.h)}
            valueText={i18n.t(`dashboard:frame.heights.${placement.h}`)}
            min={0}
            max={CARD_HEIGHTS.length - 1}
            box={box}
            grid={grid}
            onDrag={heightPx => resizeTo({ h: nearestHeight(heightPx) })}
            onStep={delta => resizeTo({ h: stepHeight(placement.h, delta) })}
          />
        </>
      )}
    >
      {card.content}
    </DashboardCard>
  );
});

const DraggableCard = (props: GridCardProps & Box) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef } =
    useDraggable({ id: props.placement.key, disabled: !props.editing });

  return (
    <div ref={setNodeRef}>
      <CardBody {...props} listeners={listeners} attributes={attributes} setActivatorNodeRef={setActivatorNodeRef} />
    </div>
  );
};

export const DashboardGridCard = ({ span, ...props }: GridCardProps & { span: number }) => {
  const { setNodeRef, node, isOver, active } = useDroppable({ id: props.placement.key, disabled: !props.editing });
  const isDragging = active?.id === props.placement.key;

  return (
    <motion.div
      ref={setNodeRef}
      layout="position"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: isDragging ? 0.4 : 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={CARD_TRANSITION}
      className={clsx(
        "w-full col-span-12",
        `xl:col-span-${span}`,
        isOver && !isDragging && "ring-2 ring-theme-focus md:rounded-lg"
      )}
    >
      <DraggableCard {...props} box={node} />
    </motion.div>
  );
};
